/**
 * MultiSelect Component
 *
 * UI/Aesthetics: Multi-select dropdown with chips, search, and select all functionality
 * Functions:
 * - Multiple option selection with visual chips display
 * - Search/filter functionality within dropdown
 * - Select All / Deselect All toggle
 * - Alphabetical sorting of options and selected chips
 * - Configurable max chips display with overflow indicator
 * - Chip removal with delete functionality
 * - Keyboard navigation and accessibility
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  CaretDownIcon,
  XIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export interface MultiSelectOption {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  group?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: MultiSelectOption[];
  onChange: (selected: MultiSelectOption[]) => void;
  label?: string;
  labelPosition?: 'above' | 'left'; // Position of the label relative to the dropdown
  placeholder?: string;
  searchPlaceholder?: string;
  maxChips?: number;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default' | 'small'; // 'small' for MUI compatibility
  error?: boolean;
  helperText?: string;
  className?: string;
  hideChips?: boolean;
  hideChipsWhenAllSelected?: boolean;
  renderSelectedDisplay?: (
    value: MultiSelectOption[],
    totalOptionsCount: number,
  ) => React.ReactNode | null;
  renderOptionLabel?: (option: MultiSelectOption) => React.ReactNode;
  shouldAutoSort?: boolean;
  liveUpdate?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  label,
  labelPosition = 'above',
  placeholder = 'Select options...',
  searchPlaceholder = 'Search',
  maxChips = 4,
  disabled = false,
  fullWidth = false,
  size = 'default',
  error = false,
  helperText,
  className,
  hideChips = true,
  hideChipsWhenAllSelected = true,
  renderSelectedDisplay,
  renderOptionLabel,
  shouldAutoSort = true,
  liveUpdate = true,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Pending value that tracks selections while popover is open
  const [pendingValue, setPendingValue] = useState<MultiSelectOption[]>(value);

  // Sync pendingValue with value when popover opens
  useEffect(() => {
    if (open) {
      setPendingValue(value);
    }
  }, [open, value]);

  // Apply pending changes when popover closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && open && !liveUpdate) {
      onChange(pendingValue);
    }
    setSearchTerm('');
    setOpen(newOpen);
  };

  const updateSelection = (newValue: MultiSelectOption[]) => {
    setPendingValue(newValue);
    if (liveUpdate) {
      onChange(newValue); // propagate immediately if live
    }
  };

  // Convert MUI size to shadcn size
  const getButtonSize = (): 'sm' | 'default' => {
    return size === 'small' ? 'sm' : (size as 'sm' | 'default') || 'default';
  };

  // Sort options alphabetically for consistent dropdown ordering
  const sortedOptions = useMemo(
    () =>
      shouldAutoSort
        ? [...options].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
          )
        : options,
    [options, shouldAutoSort],
  );

  // Filter options based on search term (from the sorted list)
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return sortedOptions;
    return sortedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [sortedOptions, searchTerm]);

  // Sort selected chips alphabetically by label for display (only if shouldAutoSort is true)
  const sortedSelectedByLabel = useMemo(
    () =>
      shouldAutoSort
        ? [...value].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
          )
        : value,
    [value, shouldAutoSort],
  );

  const isSelected = (option: MultiSelectOption) => {
    const currentValue = open ? pendingValue : value;
    return currentValue.some((selected) => selected.id === option.id);
  };

  const handleToggle = (option: MultiSelectOption) => {
    const currentValue = open ? pendingValue : value;
    const newValue = isSelected(option)
      ? currentValue.filter((s) => s.id !== option.id)
      : [...currentValue, option];
    updateSelection(newValue);
  };

  // Grouped options logic
  const groupedOptions = useMemo(() => {
    const groups = new Map<string, MultiSelectOption[]>();
    const ungrouped: MultiSelectOption[] = [];

    filteredOptions.forEach((opt) => {
      if (opt.group) {
        if (!groups.has(opt.group)) groups.set(opt.group, []);
        groups.get(opt.group)!.push(opt);
      } else {
        ungrouped.push(opt);
      }
    });

    // Flatten groups with only 1 member
    const flattenedGroups: { group?: string; options: MultiSelectOption[] }[] =
      [];
    for (const [group, opts] of groups.entries()) {
      if (opts.length === 1) {
        flattenedGroups.push({ options: opts });
      } else {
        flattenedGroups.push({ group, options: opts });
      }
    }

    // Combine grouped + ungrouped options
    return [...flattenedGroups, { options: ungrouped }];
  }, [filteredOptions]);

  const handleSelectAll = () => {
    const currentValue = open ? pendingValue : value;
    const allSelected = filteredOptions.every((o) => isSelected(o));

    const newValue = allSelected
      ? currentValue.filter((s) => !filteredOptions.some((o) => o.id === s.id))
      : [...currentValue, ...filteredOptions.filter((o) => !isSelected(o))];

    updateSelection(newValue);
  };

  const handleGroupToggle = (
    groupName: string,
    optionsInGroup: MultiSelectOption[],
  ) => {
    const currentValue = open ? pendingValue : value;
    const allSelected = optionsInGroup.every(isSelected);
    const newValue = allSelected
      ? currentValue.filter((v) => !optionsInGroup.some((o) => o.id === v.id))
      : [...currentValue, ...optionsInGroup.filter((o) => !isSelected(o))];

    updateSelection(newValue);
  };

  // Check if all filtered options are selected
  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => isSelected(option));

  // Check if all options are selected (for hideChipsWhenAllSelected)
  const allOptionsSelected = useMemo(() => {
    if (options.length === 0) return false;
    return (
      value.length === options.length &&
      options.every((option) => value.some((v) => v.id === option.id))
    );
  }, [value, options]);

  // Render selected chips (alphabetically sorted by label)
  const renderChips = () => {
    const visibleChips = sortedSelectedByLabel.slice(0, maxChips);
    const remainingChips = sortedSelectedByLabel.slice(maxChips);
    const remainingCount = value.length - maxChips;

    return (
      <div
        className={cn('flex flex-wrap gap-1', value.length > 0 ? 'mb-2' : '')}
      >
        {visibleChips.map((option) => (
          <Tooltip key={option.id}>
            <TooltipTrigger asChild>
              <div>
                <Badge
                  variant="outline"
                  className="text-xs px-2 py-1 bg-transparent border-border text-foreground"
                >
                  {option.label.length > 15
                    ? option.label.slice(0, 15) + '...'
                    : option.label}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="whitespace-pre-line">{option.label}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge
                  variant="outline"
                  className="text-xs px-2 py-1 bg-muted text-muted-foreground border-border"
                >
                  +{remainingCount} more
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="whitespace-pre-line">
                {remainingChips.map((option) => option.label).join('\n')}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  };

  const buttonText =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? value[0].label
        : `${value.length}/${options.length} selected`;

  const labelElement = label ? (
    <Label className={cn('text-sm font-medium', error && 'text-destructive')}>
      {label}
    </Label>
  ) : null;

  return (
    <TooltipProvider>
      <div
        className={cn(labelPosition === 'above' ? 'space-y-2' : '', className)}
      >
        {labelPosition === 'above' && labelElement}

        {labelPosition === 'left' ? (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">{labelElement}</div>
            <div className="flex-1 flex flex-col">
              <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    size={getButtonSize()}
                    className={cn(
                      'justify-between font-normal',
                      !value.length && 'text-muted-foreground',
                      fullWidth ? 'w-full' : '',
                      error &&
                        'border-destructive bg-destructive/5 ring-1 ring-destructive/20',
                      className,
                    )}
                  >
                    <span className="truncate">{buttonText}</span>
                    <CaretDownIcon className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>

                <PopoverContent
                  className="p-0 z-[9999]"
                  style={{
                    minWidth: '200px',
                    maxWidth: 'min(80vw, 800px)',
                  }}
                  align="start"
                >
                  <div className="flex items-center gap-2 p-2 border-b">
                    <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        handleOpenChange(false);
                      }}
                      className="p-1 hover:bg-accent rounded-sm"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Select All / Deselect All */}
                  {filteredOptions.length > 0 && (
                    <div
                      className="flex items-center gap-2 px-3 py-2 border-b hover:bg-accent w-full text-left cursor-pointer"
                      onClick={handleSelectAll}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectAll();
                        }
                      }}
                      tabIndex={0}
                      aria-checked={
                        allFilteredSelected
                          ? 'true'
                          : filteredOptions.some((option) => isSelected(option))
                            ? 'mixed'
                            : 'false'
                      }
                      role="menuitemcheckbox"
                    >
                      <Checkbox
                        checked={
                          allFilteredSelected
                            ? true
                            : filteredOptions.some((option) =>
                                  isSelected(option),
                                )
                              ? 'indeterminate'
                              : false
                        }
                        className="pointer-events-none"
                      />
                      <span className="text-sm font-medium">
                        {allFilteredSelected ? 'Deselect All' : 'Select All'}
                      </span>
                    </div>
                  )}

                  {/* Options list */}
                  <div
                    className="max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                    onWheel={(e) => {
                      // Ensure wheel events are handled properly for scrolling
                      e.stopPropagation();
                    }}
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#d1d5db #f3f4f6',
                    }}
                  >
                    {groupedOptions.some((g) => g.options.length > 0) ? (
                      groupedOptions.map(({ group, options: groupOptions }) => (
                        <div key={group || Math.random()}>
                          {group && groupOptions.length > 1 && (
                            <div
                              className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b text-sm font-medium cursor-pointer"
                              onClick={() =>
                                handleGroupToggle(group, groupOptions)
                              }
                            >
                              <Checkbox
                                checked={
                                  groupOptions.every(isSelected)
                                    ? true
                                    : groupOptions.some(isSelected)
                                      ? 'indeterminate'
                                      : false
                                }
                              />
                              <span>{group}</span>
                            </div>
                          )}

                          {groupOptions.map((option) => {
                            const isCatchAll =
                              option.value &&
                              typeof option.value === 'object' &&
                              'isCatchAll' in option.value &&
                              option.value.isCatchAll === true;
                            return (
                              <div
                                key={option.id}
                                className={cn(
                                  'flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer',
                                  group && groupOptions.length > 1
                                    ? 'pl-8'
                                    : '',
                                  isCatchAll && 'bg-blue-50/50',
                                )}
                                onClick={() => handleToggle(option)}
                              >
                                <Checkbox checked={isSelected(option)} />
                                <span className="text-sm flex-1">
                                  {renderOptionLabel
                                    ? renderOptionLabel(option)
                                    : option.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">
                          No options found
                        </p>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {/* Selected chips - always render container to prevent shifting */}
              {!hideChips &&
                !(hideChipsWhenAllSelected && allOptionsSelected) && (
                  <div
                    className={cn(
                      'flex flex-wrap gap-1 mb-2',
                      value.length === 0 && 'opacity-0',
                    )}
                  >
                    {renderSelectedDisplay
                      ? renderSelectedDisplay(value, options.length)
                      : value.length > 0 && renderChips()}
                  </div>
                )}

              {helperText && (
                <p
                  className={cn(
                    'text-sm',
                    error ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  {helperText}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <Popover open={open} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={disabled}
                  size={getButtonSize()}
                  className={cn(
                    'justify-between font-normal',
                    !value.length && 'text-muted-foreground',
                    fullWidth ? 'w-full' : '',
                    error &&
                      'border-destructive bg-destructive/5 ring-1 ring-destructive/20',
                    className,
                  )}
                >
                  <span className="truncate">{buttonText}</span>
                  <CaretDownIcon className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className="p-0 z-[9999]"
                style={{
                  minWidth: '300px',
                  maxWidth: 'min(80vw, 800px)',
                }}
                align="start"
              >
                <div className="flex items-center gap-2 p-2 border-b">
                  <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleOpenChange(false);
                    }}
                    className="p-1 hover:bg-accent rounded-sm"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Select All / Deselect All */}
                {filteredOptions.length > 0 && (
                  <div
                    className="flex items-center gap-2 px-3 py-2 border-b hover:bg-accent w-full text-left cursor-pointer"
                    onClick={handleSelectAll}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectAll();
                      }
                    }}
                    tabIndex={0}
                    aria-checked={
                      allFilteredSelected
                        ? 'true'
                        : filteredOptions.some((option) => isSelected(option))
                          ? 'mixed'
                          : 'false'
                    }
                    role="menuitemcheckbox"
                  >
                    <Checkbox
                      checked={
                        allFilteredSelected
                          ? true
                          : filteredOptions.some((option) => isSelected(option))
                            ? 'indeterminate'
                            : false
                      }
                      className="pointer-events-none"
                    />
                    <span className="text-sm font-medium">
                      {allFilteredSelected ? 'Deselect All' : 'Select All'}
                    </span>
                  </div>
                )}

                {/* Options list */}
                <div
                  className="max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                  onWheel={(e) => {
                    // Ensure wheel events are handled properly for scrolling
                    e.stopPropagation();
                  }}
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#d1d5db #f3f4f6',
                  }}
                >
                  {groupedOptions.some((g) => g.options.length > 0) ? (
                    groupedOptions.map(({ group, options: groupOptions }) => (
                      <div key={group || Math.random()}>
                        {group && groupOptions.length > 1 && (
                          <div
                            className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b text-sm font-medium cursor-pointer"
                            onClick={() =>
                              handleGroupToggle(group, groupOptions)
                            }
                          >
                            <Checkbox
                              checked={
                                groupOptions.every(isSelected)
                                  ? true
                                  : groupOptions.some(isSelected)
                                    ? 'indeterminate'
                                    : false
                              }
                            />
                            <span>{group}</span>
                          </div>
                        )}

                        {groupOptions.map((option) => {
                          const isCatchAll =
                            option.value &&
                            typeof option.value === 'object' &&
                            'isCatchAll' in option.value &&
                            option.value.isCatchAll === true;
                          return (
                            <div
                              key={option.id}
                              className={cn(
                                'flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer',
                                group && groupOptions.length > 1 ? 'pl-8' : '',
                                isCatchAll && 'bg-blue-50/50',
                              )}
                              onClick={() => handleToggle(option)}
                            >
                              <Checkbox checked={isSelected(option)} />
                              <span className="text-sm flex-1">
                                {renderOptionLabel
                                  ? renderOptionLabel(option)
                                  : option.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        No options found
                      </p>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {/* Selected chips - always render container to prevent shifting */}
            {!hideChips &&
              !(hideChipsWhenAllSelected && allOptionsSelected) && (
                <div
                  className={cn(
                    'flex flex-wrap gap-1 mb-2',
                    value.length === 0 && 'opacity-0',
                  )}
                >
                  {renderSelectedDisplay
                    ? renderSelectedDisplay(value, options.length)
                    : value.length > 0 && renderChips()}
                </div>
              )}

            {helperText && (
              <p
                className={cn(
                  'text-sm',
                  error ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {helperText}
              </p>
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default MultiSelect;
